/**
 * i18n bootstrap — English eager, other locales lazy-loaded.
 *
 * Previously every locale file (11 × ~1,900 lines) was imported statically,
 * so every user downloaded ~20,000 lines of translations regardless of which
 * language they actually used.
 *
 * This rewrite ships English (the fallback) eagerly and lazy-loads the other
 * 10 locales only when the user's detected language needs them. The earlier
 * "lazy-load everything" attempt broke `t('key', { returnObjects: true })`
 * calls — when the bundle hadn't arrived yet, `t()` returned the key string
 * instead of the array/object the caller expected, and `.map()` on a string
 * key crashed components like <LandingPage>.
 *
 * Eagerly loading English solves that entirely: the fallback language is
 * always available, so every `t()` call has a defined result from the very
 * first render — even before the user's own locale chunk arrives.
 *
 * Bundle impact: ships 1 locale (~1,900 lines) to every user instead of 11
 * (~20,000 lines). The remaining 10 are code-split by Vite into per-locale
 * chunks and fetched on demand. Savings for the monolingual-English user
 * (the majority): ~18,000 lines / ~400–500 KB of JS. Savings for non-English
 * users: still ~16,000 lines — they download English + their own locale,
 * and only their own locale.
 *
 * Behaviour preserved vs. the original:
 *   - Same supported-language list, same labels, same RTL set
 *   - LanguageDetector reads from localStorage → navigator
 *   - <html lang> and <html dir> synced on init and on languageChanged
 *   - fallbackLng is 'en'
 *   - useSuspense default (true) — safe now that English is always loaded,
 *     so the Suspense boundary never actually triggers for a fallback miss.
 *     If the user's locale hasn't arrived yet, i18next falls back to the
 *     synchronously-available English bundle.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Eager English import — guarantees fallbackLng is always populated at
// render time. Every other locale is lazy.
import en from './locales/en';

/**
 * Each entry pairs the i18n code with the ISO 3166-1 alpha-2 country code
 * used to render a flag in the language picker. The mapping is editorial:
 * we picked the country most commonly associated with each language in
 * Western product UI (e.g. US for English, ES for Spanish, PT for
 * Portuguese, SA for Arabic). Swap a `country` field if your audience
 * skews differently — e.g. `country: 'BR'` for pt if Brazilian Portuguese
 * is the dominant variant for your readers.
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', dir: 'ltr', country: 'US' },
  { code: 'de', label: 'Deutsch', dir: 'ltr', country: 'DE' },
  { code: 'it', label: 'Italiano', dir: 'ltr', country: 'IT' },
  { code: 'es', label: 'Español', dir: 'ltr', country: 'ES' },
  { code: 'pt', label: 'Português', dir: 'ltr', country: 'PT' },
  { code: 'fr', label: 'Français', dir: 'ltr', country: 'FR' },
  { code: 'zh-CN', label: '中文（简体）', dir: 'ltr', country: 'CN' },
  { code: 'zh-TW', label: '中文（繁體）', dir: 'ltr', country: 'TW' },
  { code: 'ja', label: '日本語', dir: 'ltr', country: 'JP' },
  { code: 'ko', label: '한국어', dir: 'ltr', country: 'KR' },
  { code: 'ar', label: 'العربية', dir: 'rtl', country: 'SA' },
] as const;

export type SupportedLang = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const RTL_LANGUAGES: SupportedLang[] = ['ar'];

/** Returns 'rtl' | 'ltr' for a given language code. */
export function getDir(lang: string): 'rtl' | 'ltr' {
  return RTL_LANGUAGES.includes(lang as SupportedLang) ? 'rtl' : 'ltr';
}

// ── Dynamic locale loaders (non-English) ──────────────────────────────────────
// English is eager (imported above). Every other locale is lazy — Vite emits
// one chunk per entry and only fetches it when changeLanguage(code) is called
// or on first mount if the detected language isn't 'en'.
type LazyLang = Exclude<SupportedLang, 'en'>;

const localeLoaders: Record<LazyLang, () => Promise<{ default: Record<string, unknown> }>> = {
  de: () => import('./locales/de'),
  it: () => import('./locales/it'),
  es: () => import('./locales/es'),
  pt: () => import('./locales/pt'),
  fr: () => import('./locales/fr'),
  'zh-CN': () => import('./locales/zh-CN'),
  'zh-TW': () => import('./locales/zh-TW'),
  ja: () => import('./locales/ja'),
  ko: () => import('./locales/ko'),
  ar: () => import('./locales/ar'),
};

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

/**
 * Normalise an arbitrary i18next language token to a supported code.
 * LanguageDetector can yield "en-US", "pt-BR", "zh" etc.; we want exactly
 * one of the 11 codes we ship bundles for, with 'en' as the ultimate fallback.
 */
function normaliseToSupported(raw: string | undefined): SupportedLang {
  if (!raw) return 'en';
  if ((SUPPORTED_CODES as readonly string[]).includes(raw)) return raw as SupportedLang;
  // "en-US" → "en", "pt-BR" → "pt"
  const base = raw.split('-')[0];
  if ((SUPPORTED_CODES as readonly string[]).includes(base)) return base as SupportedLang;
  // "zh" alone is ambiguous between zh-CN and zh-TW — default to zh-CN
  if (base === 'zh') return 'zh-CN';
  return 'en';
}

/**
 * Load a non-English locale bundle and register it with i18next. Idempotent:
 * if the bundle is already loaded, resolves immediately without re-importing.
 *
 * Exported so callers (e.g. LanguageSwitcher) can preload the target bundle
 * before calling `i18n.changeLanguage`. Preloading is important because
 * react-i18next rerenders on `languageChanged` but not on `added` (the event
 * fired by `addResourceBundle`). If we change the language first and load
 * after, the rerender runs against an empty bundle, falls back to English,
 * and never rerenders again when the bundle finally arrives.
 */
export async function ensureLocaleLoaded(lang: string): Promise<SupportedLang> {
  const code = normaliseToSupported(lang);
  // English is always present (imported eagerly) — nothing to do.
  if (code === 'en') return 'en';
  if (i18n.hasResourceBundle(code, 'translation')) return code;

  try {
    const mod = await localeLoaders[code]();
    i18n.addResourceBundle(code, 'translation', mod.default, true, true);
  } catch (err) {
    // Swallow — fall back to English (always present).
    // eslint-disable-next-line no-console
    console.warn(`[i18n] failed to load locale "${code}":`, err);
  }
  return code;
}

// ── Initialise with English bundled; other locales arrive on demand ───────────
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_CODES as readonly string[] as string[],
    nonExplicitSupportedLngs: false,
    load: 'currentOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'vellum-lang',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    keySeparator: '.',
    // react-i18next rerender triggers. Default is `bindI18n: 'languageChanged'`
    // only — which means if a call path ever changes the language before the
    // target bundle has finished loading (via addResourceBundle), components
    // rerender once against the empty bundle, fall back to English, and never
    // rerender again when the bundle arrives. Listening to the store's `added`
    // event covers that case as a safety net; the primary flow preloads the
    // bundle first via ensureLocaleLoaded() so this rarely fires in practice.
    react: {
      bindI18n: 'languageChanged',
      bindI18nStore: 'added',
    },
  });

// ── Load the detected locale (if not English) and sync <html lang>/<dir> ──────
//
// The code below is BROWSER-ONLY. This file is imported by both the client
// entry AND the server (entry-server.tsx → AppRoutes), and the server has no
// `document`, `localStorage`, or `navigator`. The `typeof document` guard
// prevents:
//
//   1. `document.documentElement.lang = ...` crashing the SSR bundle at
//      module load (the error that brought you here).
//   2. `i18n.on('languageChanged', ...)` firing on the server — not a crash,
//      but pointless work since the server never changes language.
//
// For SSR we intentionally stay on the eagerly-loaded English bundle. That
// matches what anonymous crawler traffic sees and avoids pulling per-locale
// chunks we won't render against. The client re-hydrates, language detection
// kicks in, and if the user's locale differs from English, the IIFE here
// loads the right bundle and re-renders the tree.

function applyHtmlAttrs(lang: string): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = getDir(lang);
}

if (typeof document !== 'undefined') {
  // If the detected language is non-English, fetch its bundle and switch to
  // it. Until that finishes, every t() call resolves via the English fallback
  // — so there's no flash of bare keys, and returnObjects calls return real
  // arrays/objects from turn one.
  void (async () => {
    const detected = normaliseToSupported(i18n.language);
    if (detected !== 'en') {
      await ensureLocaleLoaded(detected);
      if (i18n.language !== detected) {
        await i18n.changeLanguage(detected);
      }
    }
    applyHtmlAttrs(detected);
  })();

  // Reload on language change — the user switched languages mid-session.
  i18n.on('languageChanged', (lang) => {
    void ensureLocaleLoaded(lang).then((code) => applyHtmlAttrs(code));
  });
}

export default i18n;
